// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import React, { ReactElement, useState } from 'react';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { FormSubmitButton, FormSubmitOptions } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { FormInstance } from 'antd';
import { PageTemplateInput, PageTemplateOutput } from 'thehive-sdk';

import './PageTemplateForm.scss';

export interface PageTemplateFormValues extends FormSubmitOptions {
    pageTemplates?: PageTemplateOutput[];
    title: string;
    category: string;
    content: string;
}

interface PageTemplateFormProps {
    allCategories?: string[];
    form: FormInstance<PageTemplateFormValues>;
    defaultValues?: PageTemplateInput;
    disabled: boolean;
    noContent?: boolean;

    onFinish: (values: PageTemplateFormValues) => void;
    onValuesChange: () => void;
    onCancel: () => void;
}

export const PageTemplateForm = ({
    allCategories = [],
    form,
    defaultValues,
    disabled,
    noContent = false,
    onFinish,
    onValuesChange,
    onCancel,
}: PageTemplateFormProps): ReactElement => {
    const i = useLocalMessage();
    const [currentCategoryLength, setCurrentCategoryLength] = useState(0);

    return (
        <THForm<PageTemplateFormValues>
            className="page-template-manage__form"
            form={form}
            onFinish={onFinish}
            onValuesChange={onValuesChange}
            initialValues={defaultValues}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        disabled={disabled}
                        onCancel={onCancel}
                        onAnother={
                            !defaultValues
                                ? () => {
                                      form.setFieldsValue({
                                          another: true,
                                      });
                                      form.submit();
                                  }
                                : undefined
                        }
                    />
                }
            >
                <THForm.Item
                    name="title"
                    label={i('page-template.create.form.title.label')}
                    rules={[{ required: true, message: i('page-template.create.form.title.required') }]}
                >
                    <THInput placeholder={i('page-template.create.form.title.placeholder')} />
                </THForm.Item>

                <THForm.Item
                    name="category"
                    label={i('page-template.create.form.category.label')}
                    rules={[
                        { required: true, message: i('page-template.create.form.category.required') },
                        {
                            max: 128,
                            message: i('page-template.create.form.category.max-char', {
                                curr: currentCategoryLength,
                            }),
                        },
                    ]}
                >
                    <THAutoComplete
                        filtered
                        placeholder={i('page-template.create.form.category.placeholder')}
                        onChange={(e: string) => {
                            setCurrentCategoryLength(e.length);
                        }}
                        options={allCategories.map((value) => ({ label: value, value }))}
                    />
                </THForm.Item>

                {!noContent && (
                    <THForm.Item name="content" label={i('page-template.create.form.content.label')}>
                        <THMarkdown sameSize className="page-template-form__content" />
                    </THForm.Item>
                )}
            </THForm.Layout>
        </THForm>
    );
};
